import React from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import WidgetRenderer from './WidgetRenderer';
// import '../styles/ElementorCanvas.css';

const ElementorCanvas = ({ 
  sections, 
  selectedSection, 
  selectedElement,
  setSelectedSection,
  setSelectedElement,
  setShowSettings,
  onUpdateSection,
  onDeleteSection,
  onDuplicateSection,
  onAddWidget,
  onUpdateWidget,
  onDeleteWidget,
  onMoveWidget,
  onMoveSection,
  onOpenWidgetSettings,
  deviceView 
}) => {
  
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const { source, destination, type } = result;
    
    if (type === 'section') {
      onMoveSection(source.index, destination.index);
    }
  };
  
  const handleDrop = (e, sectionId, columnId) => {
    e.preventDefault();
    const widgetType = e.dataTransfer.getData('widgetType');
    const widgetId = e.dataTransfer.getData('widgetId');
    const sourceSectionId = e.dataTransfer.getData('sourceSectionId');
    const sourceColumnId = e.dataTransfer.getData('sourceColumnId');

    if (widgetType) {
      onAddWidget(sectionId, columnId, widgetType);
      return;
    }

    if (widgetId && onMoveWidget) {
      onMoveWidget(widgetId, sourceSectionId, sourceColumnId, sectionId, columnId);
    }
  };
  
  const getDeviceWidth = () => {
    switch(deviceView) {
      case 'tablet': return '768px';
      case 'mobile': return '375px';
      default: return '100%';
    }
  };
  
  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="elementor-canvas" style={{ maxWidth: getDeviceWidth(), margin: '0 auto' }}>
        <Droppable droppableId="canvas-sections" type="section">
          {(provided) => (
            <div 
              className="canvas-container" 
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {sections.map((section, sectionIndex) => (
                <Draggable key={section.id} draggableId={section.id} index={sectionIndex}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`builder-section ${selectedSection?.id === section.id ? 'selected' : ''} ${snapshot.isDragging ? 'dragging' : ''}`}
                      onClick={() => {
                        setSelectedSection(section);
                        setSelectedElement(null);
                        setShowSettings(true);
                      }}
                    >
                      <div className="section-controls">
                        <div 
                          className="section-drag-handle" 
                          {...provided.dragHandleProps}
                          style={{ cursor: snapshot.isDragging ? 'grabbing' : 'grab' }}
                        >
                          ⋮⋮
                        </div>
                        <button className="section-control" onClick={(e) => {
                          e.stopPropagation();
                          onDuplicateSection(section);
                        }}>📋 Duplicate</button>
                        <button className="section-control delete" onClick={(e) => {
                          e.stopPropagation();
                          onDeleteSection(section.id);
                        }}>🗑️ Delete</button>
                      </div>
                      
                      <div 
                        className="section-content"
                        style={{
                          backgroundColor: section.settings.backgroundColor,
                          padding: `${section.settings.padding.top}px ${section.settings.padding.right}px ${section.settings.padding.bottom}px ${section.settings.padding.left}px`,
                          margin: `${section.settings.margin?.top || 0}px ${section.settings.margin?.right || 0}px ${section.settings.margin?.bottom || 0}px ${section.settings.margin?.left || 0}px`,
                          borderRadius: `${section.settings.borderRadius}px`,
                          boxShadow: section.settings.boxShadow,
                          backgroundImage: section.settings.backgroundImage ? `url(${section.settings.backgroundImage})` : 'none',
                          backgroundAttachment: section.settings.backgroundAttachment,
                          backgroundPosition: section.settings.backgroundPosition,
                          backgroundRepeat: section.settings.backgroundRepeat,
                          backgroundSize: section.settings.backgroundSize
                        }}
                      >
                        <div className="columns-container">
                          {section.columns.map((column, colIndex) => (
                            <div 
                              key={column.id}
                              className="builder-column"
                              style={{ 
                                maxWidth: `${column.width}%`,
                                padding: column.settings?.padding ? 
                                  `${column.settings.padding.top}px ${column.settings.padding.right}px ${column.settings.padding.bottom}px ${column.settings.padding.left}px` : '0',
                                backgroundColor: column.settings?.backgroundColor || 'transparent'
                              }}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={(e) => handleDrop(e, section.id, column.id)}
                            >
                              <div className="column-header">
                                <span className="column-label">Column {colIndex + 1}</span>
                              </div>
                              
                              <div className="widgets-container">
                                {column.widgets.map((widget) => (
                                  <WidgetRenderer 
                                    key={widget.id}
                                    widget={widget}
                                    isSelected={selectedElement?.id === widget.id}
                                    onSelect={() => {
                                      setSelectedElement({ ...widget, sectionId: section.id, columnId: column.id });
                                      setSelectedSection(section);
                                      setShowSettings(true);
                                    }}
                                    onUpdate={(updates) => onUpdateWidget(section.id, column.id, widget.id, updates)}
                                    onDelete={() => onDeleteWidget(section.id, column.id, widget.id)}
                                    onOpenSettings={() => {
                                      setSelectedElement({ ...widget, sectionId: section.id, columnId: column.id });
                                      setShowSettings(true);
                                    }}
                                    sectionId={section.id}
                                    columnId={column.id}
                                  />
                                ))}
                                
                                {column.widgets.length === 0 && (
                                  <div className="empty-column">
                                    <p>Drop a widget here</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
        
        {sections.length === 0 && (
          <div className="empty-canvas">
            <div className="empty-message">
              <p>Add a section from the sidebar to begin</p>
            </div>
          </div>
        )}
      </div>
    </DragDropContext>
  );
};

export default ElementorCanvas;